import { ref, computed } from 'vue'

const STORAGE_KEY = 'uprint_auth_token'

// Singleton state
const user = ref(null)          // { userId, userName, allowedDepts[] } | null
const isValidating = ref(false)
const authError = ref(null)

async function validateToken(token) {
  const resp = await fetch(`/api/auth/validate?token=${encodeURIComponent(token)}`)
  const data = await resp.json()

  if (resp.ok && data.success) {
    user.value = {
      userId: data.userId,
      userName: data.userName,
      allowedDepts: Array.isArray(data.allowedDepts) ? data.allowedDepts : [],
      isPrompt: !!data.isPrompt,
      isUsersAdmin: !!data.isUsersAdmin,
    }
    // Persist token for lifetime access
    localStorage.setItem(STORAGE_KEY, token)
    return true
  } else {
    return data.message || 'Token tidak valid atau sudah kedaluwarsa'
  }
}

async function validateFromUrl() {
  // Extract token from path, e.g. /550e8400-e29b-41d4-a716-446655440000
  const pathToken = window.location.pathname.replace(/^\//, '').split('/')[0]

  isValidating.value = true
  authError.value = null

  try {
    if (pathToken) {
      // Login via URL token
      const result = await validateToken(pathToken)
      if (result === true) {
        // Hide token from URL bar after successful auth
        window.history.replaceState(null, '', '/')
      } else {
        authError.value = result
      }
    } else {
      // No token in URL — try persisted token from localStorage
      const savedToken = localStorage.getItem(STORAGE_KEY)
      if (savedToken) {
        const result = await validateToken(savedToken)
        if (result !== true) {
          // Saved token no longer valid — clear it
          localStorage.removeItem(STORAGE_KEY)
          authError.value = result
        }
      } else {
        authError.value = 'Token tidak ditemukan di URL. Hubungi admin untuk mendapatkan link akses.'
      }
    }
  } catch (err) {
    authError.value = 'Gagal terhubung ke server. Coba muat ulang halaman.'
  } finally {
    isValidating.value = false
  }
}

// Returns true if the given deptId is in this user's allowedDepts
function isAllowed(deptId) {
  return user.value?.allowedDepts.includes(deptId) ?? false
}

// Convenience computed: true if user has access to ALL depts
const isFullAccess = computed(() => {
  if (!user.value) return false
  return user.value.allowedDepts.length >= 7
})

// Convenience computed: true if user can access the Prompt tab
const canAccessPrompt = computed(() => !!user.value?.isPrompt)

// Convenience computed: true if user can access the Users tab
const canAccessUsers = computed(() => !!user.value?.isUsersAdmin)

export function useAuth() {
  return {
    user,
    isValidating,
    authError,
    isFullAccess,
    canAccessPrompt,
    canAccessUsers,
    validateFromUrl,
    isAllowed,
  }
}
